import React, { useRef, useState, useCallback, DragEventHandler } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload } from 'antd';
import { GlobalVariable } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { BinaryFile, DataType, SupportedTypes } from '@kemu-io/hs-types';
import styles from '../GlobalVariablesPanel.module.css';
import classNames from 'classnames';
import byteSize from 'byte-size';
import { useTranslation } from '@hooks/index';
import useGlobalNotification from '@hooks/useGlobalNotification';

type Props = {
  variable: GlobalVariable;
  onChange: (name: string, value: SupportedTypes, type: DataType) => void;
}

const getHumanSize = (bytes: number) => {
  const size = byteSize(bytes || 0);
  return `${size.value} ${size.unit}`;
};

/**
 * Binary file control component for global variables
 * @param variable - The global variable configuration
 * @param onChange - Callback when value changes
 */
const BinaryFileControl = ({ variable, onChange }: Props) => {
  const currentValue = variable.lastValue as BinaryFile | null;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropActive, setDropActive] = useState(false);
  const t = useTranslation('GlobalVariablesPanel');
  const { showNotification } = useGlobalNotification();

  /**
   * Process file upload (both from click and drag-drop)
   */
  const processFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as ArrayBuffer;
      if(variable.controlSettings?.maxSize && result.byteLength > variable.controlSettings?.maxSize) {
        showNotification({
          type: 'error',
          title: t('BinaryFileControl.FileTooLarge.Title'),
          message: t('BinaryFileControl.FileTooLarge.Message', {
            maxSize: getHumanSize(variable.controlSettings.maxSize),
            currentSize: getHumanSize(result.byteLength),
          }),
        });
        return;
      }
      const binaryFile: BinaryFile = {
        format: file.type,
        fileName: file.name,
        data: result,
      };
      
      onChange(variable.name, binaryFile, DataType.BinaryFile);
    };

    reader.readAsArrayBuffer(file);
  }, [variable.name, onChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  /**
   * Handle custom upload request from Ant Design Upload component
   */
  const handleCustomRequest = useCallback((options: any) => {
    const file = options.file as File;
    processFile(file);
    setDropActive(false);
  }, [processFile]);

  /**
   * Handle drag enter event
   */
  const handleDragEnter: DragEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setDropActive(true);
  };

  /**
   * Handle drag leave event
   */
  const handleDragLeave: DragEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setDropActive(false);
  };

  if (currentValue) {
    // Extract filename from base64 data or use a default name
    const filename = `file_${currentValue.fileName || variable.name.replace(' ', '_')}`;
    
    return (
      <div className={styles.FileControlContainer}>
        <div className={styles.FileControlWithInfo}>
          <div
            onDragOver={handleDragEnter}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDragLeave}
          >
            <Upload
              listType="text"
              className={classNames(styles.ControlFile, styles.DropZone, {
                [styles.Active]: dropActive,
              })}
              showUploadList={false}
              customRequest={handleCustomRequest}
              openFileDialogOnClick={false}
            >
              <div className={styles.ControlFile}>
                <span style={{ padding: 6 }}>
                  <div className={styles.FileName}>{filename}</div>
                  <span className={styles.FileControlInfo}> ({getHumanSize(currentValue.data.byteLength)})</span>
                </span>
                <input
                  ref={inputRef}
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>
            </Upload>
          </div>
        </div>
        <Button 
          type="text" 
          className={styles.ControlFileButton}
          onClick={() => inputRef.current?.click()}
        >
          {t('BinaryFileControl.Change')}
        </Button>
      </div>
    );
  }

  return (
    <div
      onDragOver={handleDragEnter}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDragLeave}
    >
      <Upload
        listType="text"
        className={classNames(styles.ControlFile, styles.Empty, styles.DropZone, {
          [styles.Active]: dropActive,
        })}
        accept={variable.controlSettings?.allowedMimeTypes?.join(',')}
        maxCount={1}
        showUploadList={false}
        customRequest={handleCustomRequest}
        openFileDialogOnClick={false}
      >
        <div>
          <UploadOutlined className={styles.ControlFileIcon} />
          <Button 
            type="text" 
            className={styles.ControlFileButton}
            onClick={() => inputRef.current?.click()}
          >
            {t('BinaryFileControl.UploadFile')}
          </Button>
          <input
            ref={inputRef}
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </Upload>
    </div>
  );
};

export default BinaryFileControl;
